// Meta Pixel (Facebook Pixel) integration
import { store } from '../app/store.js';
import { registerAnalyticsSink } from '../app/analytics.js';
import { getMetaPixelId } from '../config/env';
import { getPendingPurchase, consumePendingPurchase } from '../utils/purchase-tracking';
import { isDevelopment } from '../utils/environment';
import { getExperimentProperties } from '../config/experiments';

let pixelInitialized = false;

// Initialize Meta Pixel
export async function initMetaPixel() {
  if (pixelInitialized) return;

  // Per-tier pixel id from env config; empty = pixel disabled for this tier
  // (sandbox stays disabled until a sandbox Meta dataset exists).
  const pixelId = getMetaPixelId();
  if (!pixelId) {
    console.warn('Meta Pixel disabled: no pixel id configured for this environment');
    return;
  }

  try {
    // Load Meta Pixel script
    await loadMetaPixelScript();

    if (window.fbq) {
      // Initialize pixel
      window.fbq('init', pixelId);

      // Track page view
      window.fbq('track', 'PageView');

      pixelInitialized = true;
      console.log('✅ Meta Pixel initialized');

      // Set up event listeners
      setupEventListeners();

      // Receive funnel events from the central track() — including the replay
      // of events buffered while consent was pending.
      registerAnalyticsSink('meta', forwardTrackedEvent);
    }
  } catch (error) {
    console.error('Failed to initialize Meta Pixel:', error);
  }
}

// Load Meta Pixel script
function loadMetaPixelScript() {
  return new Promise((resolve) => {
    // Check if already loaded
    if (window.fbq) {
      resolve();
      return;
    }

    // Facebook Pixel Code
    !function(f,b,e,v,n,t,s)
    {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
    n.callMethod.apply(n,arguments):n.queue.push(arguments)};
    if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
    n.queue=[];t=b.createElement(e);t.async=!0;
    t.src=v;s=b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t,s)}(window, document,'script',
    'https://connect.facebook.net/en_US/fbevents.js');

    // Resolve after a short delay to ensure script is loaded
    setTimeout(resolve, 100);
  });
}

// Sink for the central track() pipeline: every funnel event is forwarded as a
// custom event (same as the old inline fbq branch in analytics.ts), and
// screen_view additionally maps to the standard ViewContent event (previously
// emitted by a duplicate `analytics:screen` listener here).
function forwardTrackedEvent(eventName, properties = {}) {
  if (!window.fbq) return;

  window.fbq('trackCustom', eventName, properties);

  if (eventName === 'screen_view' && properties.screen_name) {
    trackEvent('ViewContent', {
      content_name: properties.screen_name,
      content_category: 'screen'
    });
  }
}

// Set up event listeners for tracking
function setupEventListeners() {
  // Track when user reaches certain milestones
  document.addEventListener('screen:loaded', (event) => {
    const screen = event.detail?.screen;

    // Track key milestones
    switch (screen?.id) {
      case 'email':
        trackEvent('Lead', {
          content_name: 'Email Capture Screen'
        });
        break;

      case 'results':
        trackEvent('CompleteRegistration', {
          status: 'analysis_complete',
          value: store.get('email')
        });
        break;

      case 'paywall':
        // No value here — the real amount isn't known until a plan is chosen.
        trackEvent('InitiateCheckout', {
          content_name: 'Paywall Screen'
        });
        break;

      case 'success': {
        // Real purchase data recorded at checkout time (sessionStorage — it
        // survives 3DS redirects). Consuming it makes a reload of #success a
        // no-op; without a pending purchase the event is skipped entirely.
        // The eventID dedups this browser event against the worker's
        // Conversions-API Purchase (same id via metadata.meta_event_id).
        const pending = consumePendingPurchase();
        if (!pending) break;
        trackEvent('Purchase', {
          value: pending.value,
          currency: pending.currency,
          content_ids: [pending.planId],
          content_type: 'product',
          num_items: 1
        }, { eventID: pending.eventId });
        break;
      }
    }
  });

  // Track checkout events
  document.addEventListener('checkout:open', () => {
    const pending = getPendingPurchase();
    const params = { content_type: 'product' };
    if (pending) {
      params.content_ids = [pending.planId];
      params.value = pending.value;
      params.currency = pending.currency;
    }
    trackEvent('AddToCart', params);
  });

  // Track form submissions
  document.addEventListener('form:submit', (event) => {
    const formName = event.detail?.form_name;
    trackEvent('SubmitApplication', {
      fb_content_name: formName
    });
  });
}

// Track event to Meta Pixel.
// options.eventID becomes fbq's 4th argument — the browser/server dedup key
// shared with the worker's Conversions-API events.
export function trackEvent(eventName, parameters = {}, options = {}) {
  if (!window.fbq) {
    console.warn('Meta Pixel not initialized');
    return;
  }

  // Add common parameters. A/B arms are merged here because the standard
  // events (Purchase/InitiateCheckout/Lead/…) fire from DOM listeners and
  // bypass the central track() enricher that stamps them everywhere else.
  const enrichedParams = {
    ...getExperimentProperties(),
    ...parameters,
    source: 'web2app',
    version: 'v5',
    user_id: localStorage.getItem('lunai_user_id')
  };

  // Standard events use 'track', custom events use 'trackCustom'
  const standardEvents = [
    'PageView', 'ViewContent', 'Search', 'AddToCart', 'AddToWishlist',
    'InitiateCheckout', 'AddPaymentInfo', 'Purchase', 'Lead',
    'CompleteRegistration', 'Contact', 'CustomizeProduct', 'Donate',
    'FindLocation', 'Schedule', 'StartTrial', 'SubmitApplication', 'Subscribe'
  ];

  const method = standardEvents.includes(eventName) ? 'track' : 'trackCustom';
  if (options.eventID) {
    window.fbq(method, eventName, enrichedParams, { eventID: options.eventID });
  } else {
    window.fbq(method, eventName, enrichedParams);
  }

  // Log in development
  if (isDevelopment()) {
    console.log('📊 Meta Pixel Event:', eventName, enrichedParams, options);
  }
}

// Track custom conversions
export function trackCustomConversion(conversionName, value = null) {
  trackEvent(conversionName, {
    value: value,
    currency: 'USD'
  });
}

// Update user data for better targeting
export function updatePixelUserData(userData) {
  if (!window.fbq) return;

  // Meta Pixel advanced matching
  const advancedMatching = {};

  if (userData.email) {
    advancedMatching.em = userData.email.toLowerCase();
  }

  if (userData.firstName) {
    advancedMatching.fn = userData.firstName.toLowerCase();
  }

  if (userData.lastName) {
    advancedMatching.ln = userData.lastName.toLowerCase();
  }

  if (userData.phone) {
    advancedMatching.ph = userData.phone.replace(/\D/g, '');
  }

  if (userData.dateOfBirth) {
    advancedMatching.db = userData.dateOfBirth.replace(/-/g, '');
  }

  if (userData.gender) {
    advancedMatching.ge = userData.gender.toLowerCase()[0]; // 'm' or 'f'
  }

  if (userData.city) {
    advancedMatching.ct = userData.city.toLowerCase();
  }

  if (userData.state) {
    advancedMatching.st = userData.state.toLowerCase();
  }

  if (userData.zip) {
    advancedMatching.zp = userData.zip;
  }

  if (userData.country) {
    advancedMatching.country = userData.country.toLowerCase();
  }

  // Update pixel with advanced matching data
  const pixelId = getMetaPixelId();
  if (pixelId && Object.keys(advancedMatching).length > 0) {
    window.fbq('init', pixelId, advancedMatching);
  }
}

// Export for global access
window.metaPixel = {
  track: trackEvent,
  trackCustomConversion,
  updateUserData: updatePixelUserData
};